// Normalise a user-selected screenshot into a web-safe, backend-accepted image
// (PNG / JPEG / WebP) for Report It attachments (MV-1077).
//
// Mobile pickers — iOS in particular — frequently hand back a HEIC/HEIF file or
// a blank MIME type that the backend allowlist (and the operator preview) can't
// use. Rather than reject those, we transcode them to JPEG via a canvas, so any
// image the user can pick is accepted. iOS supplies the HEIC decoder at the OS
// level, so createImageBitmap can decode it there; where a browser genuinely
// can't decode the bytes (e.g. desktop HEIC) we surface a clear failure instead
// of uploading something unviewable. Files already in a web-safe format pass
// through untouched (no re-encode, no quality loss).

export const WEB_SAFE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const JPEG_QUALITY = 0.92;

export type NormalizeFailure = 'unsupported' | 'decode_failed';

export interface NormalizeResult {
  file?: File;
  failure?: NormalizeFailure;
}

export interface NormalizeDeps {
  /** Decode + re-encode to JPEG. Injected for tests; defaults to a canvas
   *  transcode that relies on the platform's image decoders. */
  transcodeToJpeg?: (file: File) => Promise<File | null>;
}

/**
 * Re-encode an image File to JPEG using createImageBitmap + a canvas. Returns
 * null when the platform can't decode the bytes or can't produce a blob, so the
 * caller can fail gracefully rather than upload an unreadable attachment.
 */
export async function canvasTranscodeToJpeg(file: File): Promise<File | null> {
  if (typeof createImageBitmap !== 'function') {
    return null;
  }

  let bitmap: ImageBitmap;

  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    ctx.drawImage(bitmap, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((result) => resolve(result), 'image/jpeg', JPEG_QUALITY);
    });

    if (!blob) {
      return null;
    }

    const baseName = file.name.replace(/\.[^./\\]+$/, '') || 'screenshot';

    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } finally {
    if (typeof bitmap.close === 'function') {
      bitmap.close();
    }
  }
}

/**
 * Normalise a selected file for upload. A file already in a web-safe format is
 * returned unchanged; a HEIC/HEIF or blank-MIME image is transcoded to JPEG;
 * anything that isn't an image (or can't be decoded) returns a failure reason.
 */
export async function normalizeScreenshotFile(
  file: File,
  deps: NormalizeDeps = {},
): Promise<NormalizeResult> {
  if (WEB_SAFE_IMAGE_TYPES.includes(file.type)) {
    return { file };
  }

  // Some mobile pickers report an empty MIME type for a valid image; treat
  // blank-type and any image/* (e.g. image/heic) as transcode candidates.
  const looksLikeImage = file.type === '' || file.type.startsWith('image/');

  if (!looksLikeImage) {
    return { failure: 'unsupported' };
  }

  const transcode = deps.transcodeToJpeg ?? canvasTranscodeToJpeg;
  const transcoded = await transcode(file);

  return transcoded ? { file: transcoded } : { failure: 'decode_failed' };
}
